import React, { useEffect, useState } from "react";
import { Plus, Trash2, Megaphone, Mail, MessageSquare, Phone, X } from "lucide-react";
import api from "./api";

const CHANNELS = [
  { id: "email", label: "Email", icon: Mail },
  { id: "sms", label: "SMS", icon: MessageSquare },
  { id: "call", label: "Call", icon: Phone },
  { id: "mail", label: "Direct mail", icon: Megaphone },
];

const SEGMENTS = [
  { id: "clients", label: "All current clients" },
  { id: "prospects", label: "All prospects" },
  { id: "stage:Discovery Booked", label: "Stage · Discovery Booked" },
  { id: "stage:Discovery Completed", label: "Stage · Discovery Completed" },
  { id: "stage:Active Lead", label: "Stage · Active Lead" },
  { id: "stage:Raw Lead", label: "Stage · Raw Lead" },
];

const STATUS_TONE = {
  draft: { bg: "rgba(255,255,255,0.06)", fg: "var(--crm-text-muted)" },
  scheduled: { bg: "rgba(70,120,255,0.14)", fg: "#a5c1ff" },
  active: { bg: "rgba(46,213,115,0.14)", fg: "#7bea9e" },
  paused: { bg: "rgba(255,180,64,0.14)", fg: "#ffce6e" },
  complete: { bg: "rgba(180,180,180,0.14)", fg: "var(--crm-text-muted)" },
};

export default function Campaigns() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openForm, setOpenForm] = useState(false);
  const [f, setF] = useState({
    name: "",
    goal: "",
    segment: "clients",
    channel: "email",
    body: "",
    starts_on: "",
    status: "draft",
  });

  const load = () => {
    setLoading(true);
    api.get("/crm/campaigns").then((r) => setItems(r.data.items)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const create = async (e) => {
    e.preventDefault();
    await api.post("/crm/campaigns", f);
    setOpenForm(false);
    setF({ name: "", goal: "", segment: "clients", channel: "email", body: "", starts_on: "", status: "draft" });
    load();
  };

  const del = async (id) => {
    if (!window.confirm("Delete this campaign?")) return;
    await api.delete(`/crm/campaigns/${id}`);
    load();
  };

  const setStatus = async (id, status) => {
    await api.patch(`/crm/campaigns/${id}`, { status });
    load();
  };

  return (
    <div data-testid="campaigns-page">
      <header className="flex items-center justify-between mb-6">
        <div>
          <div className="text-[11px] tracking-[0.35em] uppercase" style={{ color: "var(--crm-accent)" }}>
            Outreach
          </div>
          <h1 className="text-3xl font-light mt-1" style={{ color: "var(--crm-text)" }}>Campaigns</h1>
        </div>
        <button
          onClick={() => setOpenForm(true)}
          data-testid="new-campaign"
          className="px-4 py-2.5 rounded-full text-sm font-medium text-black inline-flex items-center gap-2"
          style={{ background: "linear-gradient(180deg,#f4d47b,#d4a53a 55%,#b78a2f)" }}
        >
          <Plus size={15} /> New campaign
        </button>
      </header>

      {loading ? (
        <div className="text-sm" style={{ color: "var(--crm-text-muted)" }}>Loading…</div>
      ) : items.length === 0 ? (
        <div className="crm-glass rounded-2xl p-10 text-center" style={{ color: "var(--crm-text-muted)" }}>
          <Megaphone size={20} className="mx-auto mb-3 opacity-50" />
          No campaigns yet — click "New campaign" to draft your first.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((c) => {
            const tone = STATUS_TONE[c.status] || STATUS_TONE.draft;
            const Icon = CHANNELS.find((x) => x.id === c.channel)?.icon || Megaphone;
            return (
              <div key={c.id} className="crm-glass rounded-2xl p-5" data-testid={`campaign-${c.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <Icon size={14} style={{ color: "var(--crm-accent)" }} />
                      <span className="text-[10px] uppercase tracking-widest" style={{ color: "var(--crm-text-muted)" }}>{c.channel}</span>
                    </div>
                    <h3 className="text-lg font-medium mt-2 truncate" style={{ color: "var(--crm-text)" }}>{c.name}</h3>
                    {c.goal && <div className="text-xs mt-1" style={{ color: "var(--crm-text-muted)" }}>{c.goal}</div>}
                  </div>
                  <span className="text-[10px] px-2.5 py-1 rounded-full uppercase tracking-widest whitespace-nowrap"
                    style={{ background: tone.bg, color: tone.fg }}>{c.status}</span>
                </div>
                <div className="mt-4 pt-4 border-t grid grid-cols-3 gap-3 text-xs" style={{ borderColor: "var(--crm-line-soft)" }}>
                  <div>
                    <div className="uppercase tracking-widest text-[9px] opacity-70" style={{ color: "var(--crm-text-muted)" }}>Segment</div>
                    <div className="mt-0.5" style={{ color: "var(--crm-text)" }}>{c.segment || "—"}</div>
                  </div>
                  <div>
                    <div className="uppercase tracking-widest text-[9px] opacity-70" style={{ color: "var(--crm-text-muted)" }}>Targets</div>
                    <div className="mt-0.5" style={{ color: "var(--crm-text)" }}>{c.target_count.toLocaleString()}</div>
                  </div>
                  <div>
                    <div className="uppercase tracking-widest text-[9px] opacity-70" style={{ color: "var(--crm-text-muted)" }}>Starts</div>
                    <div className="mt-0.5" style={{ color: "var(--crm-text)" }}>{c.starts_on || "—"}</div>
                  </div>
                </div>
                <div className="mt-4 flex items-center justify-between">
                  <div className="flex gap-1.5">
                    {["draft", "scheduled", "active", "paused", "complete"].map((s) => (
                      <button key={s} onClick={() => setStatus(c.id, s)}
                        className="text-[10px] px-2 py-1 rounded-full transition"
                        style={c.status === s
                          ? { background: STATUS_TONE[s].bg, color: STATUS_TONE[s].fg, border: "1px solid var(--crm-line)" }
                          : { color: "var(--crm-text-subtle)" }
                        }
                      >{s}</button>
                    ))}
                  </div>
                  <button onClick={() => del(c.id)} className="opacity-60 hover:opacity-100 transition" style={{ color: "var(--crm-text-muted)" }}>
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {openForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6" style={{ background: "rgba(0,0,0,0.6)", backdropFilter: "blur(6px)" }} onClick={() => setOpenForm(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={create} className="crm-glass-strong rounded-2xl p-6 max-w-xl w-full" style={{ color: "var(--crm-text)" }}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-widest" style={{ color: "var(--crm-accent)" }}>New campaign</div>
                <h3 className="text-xl font-light mt-1">Draft a new outreach</h3>
              </div>
              <button type="button" onClick={() => setOpenForm(false)} className="w-8 h-8 rounded-full crm-glass flex items-center justify-center"><X size={14} /></button>
            </div>
            <div className="space-y-3">
              <Field label="Name" value={f.name} onChange={(v) => setF({ ...f, name: v })} required testid="camp-name" />
              <Field label="Goal (e.g. 'Book annual reviews')" value={f.goal} onChange={(v) => setF({ ...f, goal: v })} testid="camp-goal" />
              <div className="grid grid-cols-2 gap-3">
                <Select label="Segment" value={f.segment} onChange={(v) => setF({ ...f, segment: v })} options={SEGMENTS} testid="camp-segment" />
                <Select label="Channel" value={f.channel} onChange={(v) => setF({ ...f, channel: v })} options={CHANNELS} testid="camp-channel" />
              </div>
              <Field label="Start date" type="date" value={f.starts_on} onChange={(v) => setF({ ...f, starts_on: v })} testid="camp-start" />
              <label className="block">
                <span className="text-[11px] uppercase tracking-widest" style={{ color: "var(--crm-text-muted)" }}>Message / notes</span>
                <textarea
                  value={f.body}
                  onChange={(e) => setF({ ...f, body: e.target.value })}
                  rows={4}
                  data-testid="camp-body"
                  className="mt-2 w-full px-3 py-2 rounded-lg text-sm crm-pane focus:outline-none resize-none"
                  style={{ background: "var(--crm-bg-elev)", color: "var(--crm-text)" }}
                />
              </label>
            </div>
            <div className="mt-5 flex justify-end gap-3">
              <button type="button" onClick={() => setOpenForm(false)} className="px-4 py-2 rounded-lg text-sm" style={{ color: "var(--crm-text-muted)" }}>Cancel</button>
              <button type="submit" data-testid="save-campaign" className="px-5 py-2 rounded-lg text-sm font-medium text-black" style={{ background: "linear-gradient(180deg,#f4d47b,#d4a53a 55%,#b78a2f)" }}>Create</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, testid, type = "text", required }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-widest" style={{ color: "var(--crm-text-muted)" }}>{label}</span>
      <input
        type={type}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="mt-2 w-full px-3 py-2 rounded-lg text-sm crm-pane focus:outline-none"
        style={{ background: "var(--crm-bg-elev)", color: "var(--crm-text)" }}
      />
    </label>
  );
}
function Select({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-widest" style={{ color: "var(--crm-text-muted)" }}>{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="mt-2 w-full px-3 py-2 rounded-lg text-sm crm-pane focus:outline-none"
        style={{ background: "var(--crm-bg-elev)", color: "var(--crm-text)" }}
      >
        {options.map((o) => (
          <option key={o.id} value={o.id}>{o.label}</option>
        ))}
      </select>
    </label>
  );
}
